import { ArrowRight, Globe2, Rocket, ShieldCheck, Zap } from "lucide-react";
import { FormEvent, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Seo } from "../components/Seo";
import { postJson } from "../lib/api";
import type { GeoInfo } from "../lib/types";

export default function HomePage() {
  const [geo, setGeo] = useState<GeoInfo | null>(null);
  const [greeting, setGreeting] = useState("Hello from the edge");
  const [variant, setVariant] = useState<"A" | "B">("A");
  const [email, setEmail] = useState("");
  const [contact, setContact] = useState({ email: "", message: "" });
  const [flash, setFlash] = useState("");

  useEffect(() => {
    void fetch("/api/geo").then((r) => r.json()).then(setGeo).catch(() => undefined);
    void fetch("/api/greeting").then((r) => r.json()).then((d) => setGreeting(d.greeting)).catch(() => undefined);
    void fetch("/api/ab").then((r) => r.json()).then((d) => setVariant(d.variant)).catch(() => undefined);
  }, []);

  const hero = useMemo(
    () =>
      variant === "A"
        ? "Launch your SaaS globally in minutes"
        : "Scale every request with Cloudflare edge power",
    [variant],
  );

  const onNewsletter = async (e: FormEvent) => {
    e.preventDefault();
    const res = await postJson<{ message: string }>("/api/newsletter", { email });
    setFlash(res.data?.message ?? res.error ?? "Failed");
    if (res.ok) setEmail("");
  };

  const onContact = async (e: FormEvent) => {
    e.preventDefault();
    const res = await postJson<{ message: string }>("/api/contact", contact);
    setFlash(res.data?.message ?? res.error ?? "Failed");
    if (res.ok) setContact({ email: "", message: "" });
  };

  return (
    <>
      <Seo title="Edge-First SaaS Starter" description="Production demo of Cloudflare Pages + Workers + Pages Functions." />

      <section className="relative overflow-hidden rounded-3xl border border-border bg-gradient-to-br from-brand/20 via-surface to-bg px-6 py-16 md:px-12">
        <div className="absolute -right-12 -top-12 h-48 w-48 rounded-full bg-brand/20 blur-3xl" />
        <div className="absolute -bottom-16 left-10 h-56 w-56 rounded-full bg-coral/20 blur-3xl" />

        <p className="mb-5 inline-flex items-center rounded-full border border-brand/40 bg-brand/10 px-3 py-1 text-xs text-brand">
          A/B Variant {variant} active at edge
        </p>
        <h1 className="font-display text-4xl leading-tight md:text-6xl">{hero}</h1>
        <p className="mt-5 max-w-2xl text-copy/80 md:text-lg">
          EdgeLaunch is a premium starter for modern SaaS teams who need static speed, edge APIs, middleware security, and deployment simplicity.
        </p>
        <div className="mt-7 flex flex-wrap gap-3">
          <Link to="/signup" className="inline-flex items-center gap-2 rounded-full bg-brand px-5 py-3 font-medium text-white hover:bg-brand-2">
            Start Building <ArrowRight size={16} />
          </Link>
          <Link to="/dashboard" className="rounded-full border border-border px-5 py-3 hover:bg-surface-2">
            Explore Dashboard
          </Link>
        </div>

        <div className="mt-8 grid gap-3 text-sm md:grid-cols-3">
          <div className="card-glow flex items-center gap-2"><Globe2 size={16} /> {geo ? `${geo.city}, ${geo.country}` : "Resolving location..."}</div>
          <div className="card-glow flex items-center gap-2"><Zap size={16} /> {greeting}</div>
          <div className="card-glow flex items-center gap-2"><ShieldCheck size={16} /> Protected by edge middleware</div>
        </div>
      </section>

      <section className="mt-12 grid gap-6 md:grid-cols-3">
        {["Pages + Functions", "Worker-like middleware", "D1/KV/R2 ready"].map((item, i) => (
          <article key={item} className="card-glow reveal" style={{ animationDelay: `${i * 80}ms` }}>
            <Rocket size={20} className="text-brand" />
            <h3 className="mt-3 font-display text-xl">{item}</h3>
            <p className="mt-2 text-copy/70">Structured for real teams, with typed APIs, auth gates, and scalable route boundaries.</p>
          </article>
        ))}
      </section>

      <section className="mt-12 grid gap-4 md:grid-cols-2">
        <img
          src="https://images.unsplash.com/photo-1551434678-e076c223a692?auto=format&fit=crop&w=900&q=80"
          alt="Team collaborating in modern office"
          loading="lazy"
          className="h-56 w-full rounded-2xl object-cover"
        />
        <img
          src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=900&q=80"
          alt="Analytics dashboard on laptop"
          loading="lazy"
          className="h-56 w-full rounded-2xl object-cover"
        />
      </section>

      <section className="mt-12 grid gap-8 md:grid-cols-2">
        <form onSubmit={onNewsletter} className="card-glow">
          <h2 className="font-display text-2xl">Newsletter at the Edge</h2>
          <p className="mt-2 text-copy/70">Subscribe through a Cloudflare Pages Function endpoint.</p>
          <input
            required
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@startup.com"
            className="mt-4 w-full rounded-xl border border-border bg-surface px-4 py-3"
          />
          <button className="mt-3 rounded-xl bg-brand px-4 py-2 text-white">Join Newsletter</button>
        </form>

        <form onSubmit={onContact} className="card-glow">
          <h2 className="font-display text-2xl">Contact Form Function</h2>
          <p className="mt-2 text-copy/70">Serverless handling with bot checks + basic rate limit.</p>
          <input
            required
            type="email"
            value={contact.email}
            onChange={(e) => setContact((s) => ({ ...s, email: e.target.value }))}
            placeholder="your email"
            className="mt-4 w-full rounded-xl border border-border bg-surface px-4 py-3"
          />
          <textarea
            required
            value={contact.message}
            onChange={(e) => setContact((s) => ({ ...s, message: e.target.value }))}
            rows={4}
            placeholder="Tell us about your project"
            className="mt-3 w-full rounded-xl border border-border bg-surface px-4 py-3"
          />
          <button className="mt-3 rounded-xl bg-coral px-4 py-2 text-white">Send Message</button>
        </form>
      </section>

      {flash && <p className="mt-6 rounded-xl border border-brand/30 bg-brand/10 px-4 py-3 text-sm">{flash}</p>}
    </>
  );
}
